'use client';

import { useState } from 'react';
import { X, Loader2, CheckCircle, AlertCircle, MapPin, Stethoscope, Calendar, Shield } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

interface ProviderInfo {
  npiNumber: string;
  name: string;
  credential: string | null;
  status: string;
  enumerationDate: string | null;
  address: {
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    postalCode: string;
  } | null;
  specialty: string | null;
}

interface NPIVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (npiNumber: string, providerInfo: ProviderInfo, zipCode: string) => void | Promise<void>;
  initialNpi?: string;
  userName?: string;
}

type VerifyState = 'input' | 'loading' | 'result' | 'success' | 'error';

export function NPIVerificationModal({ isOpen, onClose, onVerified, initialNpi = '', userName }: NPIVerificationModalProps) {
  const { t } = useLanguage();
  const [npiNumber, setNpiNumber] = useState(initialNpi);
  const [zipCode, setZipCode] = useState('');
  const [state, setState] = useState<VerifyState>('input');
  const [providerInfo, setProviderInfo] = useState<ProviderInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [validationWarning, setValidationWarning] = useState('');

  if (!isOpen) return null;

  // Helper function to normalize names for comparison
  const normalizeName = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[.,\-]/g, '');
  };

  // Validate if user name matches NPI provider name
  const validateNameMatch = (npiName: string): boolean => {
    if (!userName) return true; // Skip validation if no user name provided

    const normalizedUserName = normalizeName(userName);
    const normalizedNpiName = normalizeName(npiName);

    // Check if names contain each other (handles "John Doe" vs "Doe, John" or partial matches)
    const userNameParts = normalizedUserName.split(' ');
    const npiNameParts = normalizedNpiName.split(' ');

    // Check if at least first and last name match
    const hasMatchingParts = userNameParts.filter(part =>
      part.length > 2 && npiNameParts.some(npiPart => npiPart.includes(part) || part.includes(npiPart))
    ).length >= 2;

    return hasMatchingParts || normalizedNpiName.includes(normalizedUserName) || normalizedUserName.includes(normalizedNpiName);
  };

  // Validate if user zipcode matches NPI address zipcode
  const validateZipCodeMatch = (npiZipCode: string | undefined): boolean => {
    if (!zipCode || !npiZipCode) return true; // Skip validation if no zipcode provided

    // Compare first 5 digits of zip codes
    return npiZipCode.slice(0, 5) === zipCode.slice(0, 5);
  };

  const handleVerify = async () => {
    if (!npiNumber.trim() || npiNumber.length !== 10) {
      setErrorMessage(t.profile.npiVerification.invalidFormat);
      setState('error');
      return;
    }

    if (!zipCode.trim() || zipCode.length !== 5) {
      setErrorMessage(t.profile.npiVerification.zipInvalid);
      setState('error');
      return;
    }

    setState('loading');
    setErrorMessage('');
    setValidationWarning('');

    try {
      const res = await fetch(`/api/providers/verify-npi?number=${npiNumber}`);
      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'timeout') {
          setErrorMessage(t.profile.npiVerification.timeout);
        } else if (data.error === 'rate_limit') {
          setErrorMessage(t.profile.npiVerification.rateLimited);
        } else if (data.error === 'not_found') {
          setErrorMessage(t.profile.npiVerification.notFound);
        } else {
          setErrorMessage(data.message || t.profile.npiVerification.error);
        }
        setState('error');
        return;
      }

      const provider = data.provider;

      // Validate name match
      const nameMatches = validateNameMatch(provider.name);
      const zipCodeMatches = validateZipCodeMatch(provider.address?.postalCode);

      if (!nameMatches) {
        setErrorMessage(t.profile.npiVerification.nameMismatch);
        setState('error');
        return;
      }

      if (!zipCodeMatches) {
        setErrorMessage(t.profile.npiVerification.zipCodeMismatch);
        setState('error');
        return;
      }

      setProviderInfo(provider);
      setState('result');
    } catch (error) {
      console.error('src/components/ui/NPIVerificationModal.tsx', error);
      setErrorMessage(t.profile.npiVerification.error);
      setState('error');
    }
  };

  const handleConfirm = async () => {
    if (!providerInfo) return;
    setSaving(true);
    try {
      await onVerified(npiNumber, providerInfo, zipCode);
      setState('success');
    } catch (err) {
      // onVerified rejected — the caller (page) already surfaced the error via toast.
      // Stay in 'result' state so the user can retry or cancel.
      console.error('src/components/ui/NPIVerificationModal.tsx handleConfirm', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setState('input');
    setProviderInfo(null);
    setErrorMessage('');
    setZipCode('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div data-testid="npi-modal-root" className="relative bg-background rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <button data-testid="npi-modal-close-btn" onClick={handleClose} className="absolute top-4 right-4 text-foreground-muted hover:text-foreground z-10">
          <X size={20} />
        </button>

        <div className="p-6">
          <h2 className="text-lg font-semibold text-foreground mb-1">{t.profile.npiVerification.title}</h2>
          <p className="text-sm text-foreground-muted mb-6">{t.profile.npiVerification.subtitle}</p>

          {state === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t.profile.npiVerification.enterNpi}</label>
                <input
                  data-testid="npi-modal-npi-input"
                  type="text"
                  value={npiNumber}
                  onChange={e => setNpiNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="1234567890"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  maxLength={10}
                />
                <p className="text-xs text-foreground-muted mt-1">{t.profile.npiVerification.npiHint}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t.profile.npiVerification.zipLabel}</label>
                <input
                  data-testid="npi-modal-zip-input"
                  type="text"
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="12345"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  maxLength={5}
                />
                <p className="text-xs text-foreground-muted mt-1">{t.profile.npiVerification.zipHint}</p>
              </div>
              <button
                data-testid="npi-modal-verify-btn"
                onClick={handleVerify}
                disabled={npiNumber.length !== 10 || zipCode.length !== 5}
                className="w-full px-4 py-2 bg-foreground text-background rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition"
              >
                {t.profile.npiVerification.verify}
              </button>
            </div>
          )}

          {state === 'loading' && (
            <div className="flex flex-col items-center py-8">
              <Loader2 size={40} className="text-primary animate-spin mb-4" />
              <p className="text-foreground-muted">{t.profile.npiVerification.verifying}</p>
            </div>
          )}

          {state === 'error' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center py-4">
                <AlertCircle data-testid="npi-modal-error-icon" size={40} className="text-error mb-3" />
                <p className="text-foreground text-center">{errorMessage}</p>
              </div>
              <button data-testid="npi-modal-try-again-btn" onClick={() => setState('input')} className="w-full px-4 py-2 bg-foreground text-background rounded-lg font-medium hover:opacity-90">
                {t.profile.npiVerification.tryAgain}
              </button>
            </div>
          )}

          {state === 'result' && providerInfo && (
            <div className="space-y-4">
              {validationWarning && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle size={16} className="text-warning shrink-0 mt-0.5" />
                  <p className="text-sm text-warning">{validationWarning}</p>
                </div>
              )}
              <div className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Shield size={20} className={providerInfo.status === 'Active' ? 'text-success' : 'text-error'} />
                  <div>
                    <p className="font-medium text-foreground">{providerInfo.name}</p>
                    {providerInfo.credential && <p className="text-sm text-foreground-muted">{providerInfo.credential}</p>}
                  </div>
                </div>
                {providerInfo.specialty && (
                  <div className="flex items-center gap-3">
                    <Stethoscope size={18} className="text-foreground-muted" />
                    <p className="text-sm text-foreground">{providerInfo.specialty}</p>
                  </div>
                )}
                {providerInfo.address && (
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-foreground-muted shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground">
                      {providerInfo.address.address1}{providerInfo.address.address2 ? `, ${providerInfo.address.address2}` : ''}<br />
                      {providerInfo.address.city}, {providerInfo.address.state} {providerInfo.address.postalCode}
                    </p>
                  </div>
                )}
                {providerInfo.enumerationDate && (
                  <div className="flex items-center gap-3">
                    <Calendar size={18} className="text-foreground-muted" />
                    <p className="text-sm text-foreground">{t.profile.npiVerification.since} {providerInfo.enumerationDate}</p>
                  </div>
                )}
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${providerInfo.status === 'Active' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                  {providerInfo.status}
                </div>
              </div>
              <div className="flex gap-3">
                <button data-testid="npi-modal-result-cancel-btn" onClick={() => setState('input')} className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-sidebar-hover">
                  {t.common.cancel}
                </button>
                <button data-testid="npi-modal-use-verified-data-btn" onClick={handleConfirm} disabled={saving || providerInfo.status !== 'Active'} className="flex-1 px-4 py-2 bg-success text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
                  {saving ? '...' : t.profile.npiVerification.confirmSave}
                </button>
              </div>
              {providerInfo.status !== 'Active' && (
                <p className="text-xs text-error text-center">{t.profile.npiVerification.notActive}</p>
              )}
            </div>
          )}

          {state === 'success' && (
            <div className="flex flex-col items-center py-8">
              <CheckCircle data-testid="npi-modal-success-icon" size={48} className="text-success mb-4" />
              <p className="text-lg font-medium text-foreground mb-2">{t.profile.npiVerification.successTitle}</p>
              <p className="text-sm text-foreground-muted text-center">{t.profile.npiVerification.successMessage}</p>
              <button onClick={handleClose} className="mt-6 px-6 py-2 bg-foreground text-background rounded-lg font-medium hover:opacity-90">
                {t.common.close}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

